import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Play } from 'lucide-react';
import { StatusBadge } from './StatusBadge';
import { SourceDot } from './SourceDot';
import { domainOf, titleOf } from '../lib/url';
import { formatDuration, formatPages } from '../lib/media';

/** "▶ 12:34" for videos, "PDF · 12 pages" for pdfs, null for articles. */
export function MediaChip({ article }: { article: Pick<Article, 'type' | 'mediaDuration'> }) {
  if (article.type === 'video') {
    return (
      <span className="text-xs inline-flex items-center gap-1" style={{ color: '#5C5C72' }}>
        <Play size={11} /> {formatDuration(article.mediaDuration) ?? 'video'}
      </span>
    );
  }
  if (article.type === 'pdf') {
    return (
      <span className="text-xs inline-flex items-center gap-1" style={{ color: '#5C5C72' }}>
        <FileText size={11} /> {formatPages(article.mediaDuration) ?? 'PDF'}
      </span>
    );
  }
  return null;
}

export interface Article {
  id: string;
  title: string | null;
  author?: string | null;
  siteName?: string | null;
  url: string;
  status: 'unread' | 'reading' | 'archived' | 'filed';
  type?: 'article' | 'video' | 'pdf';
  /** video: seconds; pdf: page count. */
  mediaDuration?: number | null;
  /** video: seconds watched; pdf: current page. */
  mediaPosition?: number | null;
  readingTime?: number | null;
  readingProgress?: number | null;
  wordCount?: number | null;
  excerpt?: string | null;
  /** Lead image from extraction — used by the share preview card. */
  image?: string | null;
  content?: string | null;
  tags?: string[] | null;
  notes?: string | null;
  favorite?: boolean | null;
  vaultPath?: string | null;
  /** Label of the token that clipped this; null/absent when you added it. */
  addedBy?: string | null;
  createdAt: string;
}


interface ArticleCardProps {
  article: Article;
  featured?: boolean;
}

export function ArticleCard({ article, featured }: ArticleCardProps) {
  if (featured) {
    return (
      <div className="rounded-xl border border-white/10 p-5 hover:border-violet-500/40 transition-colors" style={{ background: '#141422' }}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <SourceDot domain={domainOf(article.url)} />
              <StatusBadge status={article.status} />
            </div>
            <Link to={`/reader/${article.id}`} className="block group">
              <h3 className="font-semibold text-white group-hover:text-violet-300 transition-colors line-clamp-2 mb-1">
                {titleOf(article)}
              </h3>
            </Link>
            {article.author && <p className="text-sm" style={{ color: '#9898AA' }}>{article.author}</p>}
          </div>
          {article.status === 'reading' && typeof article.readingProgress === 'number' && (
            <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center border-2"
              style={{ borderColor: '#10B981', color: '#10B981', fontSize: 11, fontWeight: 600 }}>
              {Math.round(article.readingProgress)}%
            </div>
          )}
        </div>
        {article.status === 'reading' && typeof article.readingProgress === 'number' && (
          <div className="mt-3 h-1 rounded-full" style={{ background: '#1A1A2E' }}>
            <div className="h-full rounded-full" style={{ width: `${article.readingProgress}%`, background: '#10B981' }} />
          </div>
        )}
        <div className="mt-3 flex items-center justify-between">
          {article.type === 'video' || article.type === 'pdf' ? (
            <MediaChip article={article} />
          ) : article.readingTime ? (
            <span className="text-xs" style={{ color: '#5C5C72' }}>{article.readingTime} min read</span>
          ) : null}
          <Link to={`/reader/${article.id}`} className="text-xs font-medium text-violet-400 hover:text-violet-300 transition-colors ml-auto inline-flex items-center gap-1">
            {article.status === 'reading' ? 'Continue' : 'Read'}
            <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 py-3 border-b border-white/5 last:border-0 hover:bg-white/5 -mx-3 px-3 rounded-lg transition-colors">
      <SourceDot domain={domainOf(article.url)} />
      <div className="flex-1 min-w-0">
        <Link to={`/reader/${article.id}`} className="group">
          <p className="text-sm font-medium text-white group-hover:text-violet-300 transition-colors line-clamp-2">{titleOf(article)}</p>
        </Link>
        {article.siteName && <p className="text-xs mt-0.5" style={{ color: '#5C5C72' }}>{article.siteName}</p>}
      </div>
      <div className="flex-shrink-0 flex flex-col items-end gap-1">
        {article.type === 'video' || article.type === 'pdf' ? (
          <MediaChip article={article} />
        ) : article.readingTime ? (
          <span className="text-xs" style={{ color: '#5C5C72' }}>{article.readingTime}m</span>
        ) : null}
        <Link to={`/reader/${article.id}`} className="text-xs text-violet-400 hover:text-violet-300 inline-flex items-center gap-1">
          Read <ArrowRight size={12} />
        </Link>
      </div>
    </div>
  );
}
